import { computed } from 'vue'

import useAccount from '@/features/auth/hooks/use-account.hook'
import { RoleEnumTypeV2 } from '@/features/auth/types'
import useGroups from '@/features/groups/hooks/use-groups.hook'
import useMatchingInformation from '@/features/matching/hooks/use-matching-information.hook'
import useMatchingSettings from '@/features/matching/hooks/use-matching-settings.hook'
import useProgram from '@/features/programs/hooks/use-program.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import usePhone from '@/features/auth/hooks/use-phone.hook'
import useProgramRoles from '@/features/users/hooks/use-program-roles.hook'
import useUser from '@/features/users/hooks/use-user.hook'

export type ProfileCompletionStep = {
  id: 'mentor-criteria' | 'mentee-criteria' | 'mentee-auto-match' | 'description' | 'picture' | 'phone'
  labelKey: string
  buttonLabelKey: string
  completed: boolean
  modal: 'mentor-matching' | 'program-criterias' | 'new-matching' | 'profile-description' | 'profile-picture' | 'phone'
  modalData?: {
    role?: 'mentor' | 'mentee'
  }
}

/**
 * Hook pour gérer la complétion du profil utilisateur
 * Gère les étapes de complétion dans l'ordre strict :
 * 1. Critères mentor (si MENTOR + matching activé)
 * 2. Critères mentee OU Trouver un mentor (si MENTEE + matching activé)
 * 3. Description de profil (toujours)
 * 4. Photo de profil (toujours)
 */
export default function useProfileCompletion() {
  const { accountRef } = useAccount()
  const accountIdRef = computed(() => accountRef.value?.id)
  const { userRef } = useUser(accountIdRef)

  const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
  const { programRef } = useProgram(selectedProgramIdRef)
  const { programRolesRef } = useProgramRoles({ programIdRef: selectedProgramIdRef })
  const { canFillCriteria, canAutoMatch } = useMatchingSettings(selectedProgramIdRef)
  const { groupsRef } = useGroups()

  // Récupérer les critères de matching
  const { isMentorCriteriaCompleteRef, isMenteeCriteriaCompleteRef } = useMatchingInformation({
    onSubmitCallback: () => null,
  })

  // Vérifier si l'utilisateur est mentor/mentee dans le programme
  const isMentorRef = computed(() => programRolesRef.value?.includes(RoleEnumTypeV2.MENTOR) ?? false)
  const isMenteeRef = computed(() => programRolesRef.value?.includes(RoleEnumTypeV2.MENTEE) ?? false)

  // Critères de matching : l'étape n'est franchie que si TOUS les champs du
  // template sont renseignés (un import partiel laisse l'étape ouverte)
  const hasMentorCriteriaRef = isMentorCriteriaCompleteRef
  const hasMenteeCriteriaRef = isMenteeCriteriaCompleteRef

  // Vérifier si le mentee a un groupe (pour l'auto-match)
  const hasGroupRef = computed(() => {
    if (!selectedProgramIdRef.value) return false
    return groupsRef.value.some((group) => group.programId === selectedProgramIdRef.value)
  })

  // Vérifier si la description est remplie
  const hasDescriptionRef = computed(() => {
    return !!userRef.value?.description && userRef.value.description.trim().length > 0
  })

  // Vérifier si la photo est remplie
  const hasPictureRef = computed(() => {
    return !!userRef.value?.picture
  })

  // Vérifier si le téléphone est renseigné
  const { phoneRef } = usePhone()
  const hasPhoneRef = computed(() => !!phoneRef.value)

  // Construire la liste des étapes disponibles selon le rôle et les settings
  const stepsRef = computed<ProfileCompletionStep[]>(() => {
    const steps: ProfileCompletionStep[] = []

    // Étape 1 : Critères mentor (si MENTOR + matching activé)
    if (isMentorRef.value && canFillCriteria.value) {
      steps.push({
        id: 'mentor-criteria',
        labelKey: 'profileCompletion.steps.mentorCriteria',
        buttonLabelKey: 'profileCompletion.buttons.fillCriteria',
        completed: hasMentorCriteriaRef.value,
        modal: 'mentor-matching',
      })
    }

    // Étape 2 : Critères mentee OU Auto-match (si MENTEE + matching activé)
    if (isMenteeRef.value && canFillCriteria.value) {
      if (canAutoMatch.value) {
        // Auto-match activé : "Trouver un mentor"
        steps.push({
          id: 'mentee-auto-match',
          labelKey: 'profileCompletion.steps.findMentor',
          buttonLabelKey: 'profileCompletion.buttons.findMentor',
          completed: hasGroupRef.value,
          modal: 'new-matching',
        })
      } else {
        // Auto-match désactivé : "Renseigner critères mentee"
        steps.push({
          id: 'mentee-criteria',
          labelKey: 'profileCompletion.steps.menteeCriteria',
          buttonLabelKey: 'profileCompletion.buttons.fillCriteria',
          completed: hasMenteeCriteriaRef.value,
          modal: 'program-criterias',
          modalData: { role: 'mentee' },
        })
      }
    }

    // Étape 3 : Description (toujours)
    steps.push({
      id: 'description',
      labelKey: 'profileCompletion.steps.description',
      buttonLabelKey: 'profileCompletion.buttons.addDescription',
      completed: hasDescriptionRef.value,
      modal: 'profile-description',
    })

    // Étape 4 : Photo de profil (toujours)
    steps.push({
      id: 'picture',
      labelKey: 'profileCompletion.steps.picture',
      buttonLabelKey: 'profileCompletion.buttons.addPicture',
      completed: hasPictureRef.value,
      modal: 'profile-picture',
    })

    // Étape 5 : Numéro de téléphone (toujours)
    steps.push({
      id: 'phone',
      labelKey: 'profileCompletion.steps.phone',
      buttonLabelKey: 'profileCompletion.buttons.addPhone',
      completed: hasPhoneRef.value,
      modal: 'phone',
    })

    return steps
  })

  // Trouver la prochaine étape à compléter (ordre strict)
  const nextStepRef = computed<ProfileCompletionStep | null>(() => {
    return stepsRef.value.find((step) => !step.completed) || null
  })

  // Calculer le pourcentage de complétion
  const completionPercentageRef = computed(() => {
    const totalSteps = stepsRef.value.length
    if (totalSteps === 0) return 100

    const completedSteps = stepsRef.value.filter((step) => step.completed).length
    return Math.round((completedSteps / totalSteps) * 100)
  })

  // Vérifier si le profil est complet
  const isProfileCompleteRef = computed(() => {
    return completionPercentageRef.value === 100
  })

  // Afficher la carte uniquement si le profil n'est pas complet
  const shouldShowCardRef = computed(() => {
    return !isProfileCompleteRef.value
  })

  return {
    stepsRef,
    nextStepRef,
    completionPercentageRef,
    isProfileCompleteRef,
    shouldShowCardRef,
  }
}
